import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {StyleSheet} from 'aphrodite';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface TransformPillBaseProps {
  disabled?: boolean;
  hasError?: boolean;
  icon?: BenchlingIconName;
  onClick?: () => void;
  onDelete?: () => void;
  text: string;
  isRunning?: boolean;
}

export default function TransformPillBase({
  disabled = false,
  hasError = false,
  icon,
  onClick,
  onDelete,
  text,
  isRunning,
}: TransformPillBaseProps): JSX.Element {
  return (
    <div
      className={css(
        styles.wrapper,
        !disabled && !isRunning && onClick != null ? styles.clickable : styles.unclickable
      )}
      onClick={disabled || isRunning ? noop : onClick}
      {...getTestProps({component: 'TransformPillBase', element: 'root', key: text})}
    >
      <div className={css(styles.pill, hasError && styles.errorPill, disabled && styles.disabledPill)}>
        {(icon != null || isRunning) && (
          <BenchlingIcon
            className={classNames(css(styles.columnTypeIcon), isRunning && 'fa-spin')}
            icon={isRunning ? 'spinner-third' : icon!}
          />
        )}
        <span className={css(styles.text)}>{text}</span>
        {!disabled && onDelete != null && (
          <BenchlingIcon
            className={css(styles.deleteIcon, hasError && styles.errorDeleteIcon)}
            icon="xmark"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  columnTypeIcon: {
    marginRight: '4px',
  },
  clickable: {
    cursor: 'pointer',
  },
  unclickable: {
    cursor: 'not-allowed',
  },
  wrapper: {
    display: 'inline-block',
    maxWidth: '100%',
  },
  pill: {
    color: Color.BLUE_500,
    backgroundColor: Color.BLUE_100,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
  },
  text: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  disabledPill: {
    backgroundColor: Color.GRAY_50,
    color: Color.GRAY_900,
  },
  errorPill: {
    color: Color.RED_600,
    backgroundColor: Color.RED_150,
  },
  deleteIcon: {
    marginLeft: 4,
    cursor: 'pointer',
    color: Color.BLUE_300,
    padding: 2,
    width: 14,
    height: 14,
    // Without this, the icon will shrink to fit the text and the hover background will be elliptical not circular
    flexShrink: 0,
    ':hover': {
      color: Color.BLUE_400,
      background: Color.BLUE_200,
      borderRadius: '100%',
    },
  },
  errorDeleteIcon: {
    color: Color.RED_600,
    ':hover': {
      color: Color.RED_600,
      background: Color.RED_200,
      borderRadius: '100%',
    },
  },
});
